import React, { useState } from 'react';
import { CardGrid } from '../components/CardGrid';
import { useLanguage } from '../context/LanguageContext';
import { Building2, UtensilsCrossed, Stethoscope, BookOpen, TrendingUp, Users, FlaskConical } from 'lucide-react';

/* ════════════════════════════════════════════════════════════════
   EVIDENCE DATA — Bilingual (EN / FR)
   Sourced exclusively from user-provided PDFs.
   Classifications: academic | industry | consumer | model
   ════════════════════════════════════════════════════════════════ */

const evidenceData = {
  hotels: {
    en: [
      {
        classification: 'academic',
        stat: '0.99%–1.42%',
        headline: 'RevPAR increase per 1% reputation improvement',
        context: 'A peer-reviewed study found each 1% improvement in a hotel\'s online reputation score was associated with a 0.99% increase in RevPAR. Cornell found up to 1.42% for midscale hotels specifically.',
        source: 'Cornell Center for Hospitality Research (2012); Anderson & Lawrence, peer-reviewed study',
      },
      {
        classification: 'academic',
        stat: '13.5%',
        headline: 'Increase in booking likelihood',
        context: 'A one-point increase in average user rating on a 5-point scale made potential customers 13.5% more likely to book that hotel. This finding relates to a full one-point movement on a 5-point rating scale.',
        source: 'Cornell Center for Hospitality Research (2012)',
      },
      {
        classification: 'academic',
        stat: '11.2%',
        headline: 'Pricing power retained',
        context: 'Moving a hotel\'s average user rating up one point on a 5-point scale could allow an 11.2% price increase while maintaining the same occupancy and market share. This finding relates to a full one-point movement on a 5-point rating scale.',
        source: 'Cornell Center for Hospitality Research (2012)',
      },
      {
        classification: 'academic',
        stat: '+0.12 stars',
        headline: 'Rating increase from responding to reviews',
        context: 'Hotels that began responding to reviews saw a 0.12-star increase in ratings and a 12% increase in review volume.',
        source: 'Proserpio & Zervas, Marketing Science (2017)',
      },
      {
        classification: 'industry',
        stat: '63%',
        headline: 'Room cleanliness as top negative review trigger',
        context: 'The top triggers for negative hotel reviews include room cleanliness (63%), odors (58%), and pests (57%), followed by property cleanliness (42%) and in-room noise (31%).',
        source: 'Ecolab / AHLA hospitality consumer findings (2025)',
      },
      {
        classification: 'consumer',
        stat: '93%',
        headline: 'Travelers influenced by online reviews',
        context: '93% of travelers reported that online reviews affect their booking decisions.',
        source: 'TripAdvisor / industry research',
      },
      {
        classification: 'model',
        stat: '$44,550–$63,900',
        headline: 'Illustrative annual room-revenue upside',
        context: 'For an illustrative 100-room midscale hotel generating $4.5M in annual room revenue, a 1% improvement in online reputation score could translate to this annual upside. This is a YUL ON TAP illustrative model based on the published 0.99%–1.42% RevPAR elasticities and stated assumptions — not a directly measured outcome.',
        source: 'YUL ON TAP illustrative model based on Cornell and peer-reviewed RevPAR relationships',
      },
    ],
    fr: [
      {
        classification: 'academic',
        stat: '0,99 %–1,42 %',
        headline: 'Augmentation du RevPAR par amélioration de 1 % de la réputation',
        context: 'Une étude examinée par des pairs a révélé que chaque amélioration de 1 % du score de réputation en ligne d\'un hôtel était associée à une augmentation de 0,99 % du RevPAR. Cornell a trouvé jusqu\'à 1,42 % pour les hôtels de catégorie intermédiaire spécifiquement.',
        source: 'Cornell Center for Hospitality Research (2012); Anderson & Lawrence, étude examinée par des pairs',
      },
      {
        classification: 'academic',
        stat: '13,5 %',
        headline: 'Augmentation de la probabilité de réservation',
        context: 'Une augmentation d\'un point de la note moyenne des utilisateurs sur une échelle de 5 points rendait les clients potentiels 13,5 % plus susceptibles de réserver cet hôtel. Ce résultat se rapporte à un mouvement complet d\'un point sur une échelle de notation de 5 points.',
        source: 'Cornell Center for Hospitality Research (2012)',
      },
      {
        classification: 'academic',
        stat: '11,2 %',
        headline: 'Pouvoir de tarification maintenu',
        context: 'Augmenter la note moyenne d\'un hôtel d\'un point sur une échelle de 5 points pourrait permettre une hausse de prix de 11,2 % tout en maintenant le même taux d\'occupation et la même part de marché. Ce résultat se rapporte à un mouvement complet d\'un point sur une échelle de notation de 5 points.',
        source: 'Cornell Center for Hospitality Research (2012)',
      },
      {
        classification: 'academic',
        stat: '+0,12 étoile',
        headline: 'Augmentation de la note en répondant aux avis',
        context: 'Les hôtels qui ont commencé à répondre aux avis ont vu une augmentation de 0,12 étoile de leur note et une hausse de 12 % du volume d\'avis.',
        source: 'Proserpio & Zervas, Marketing Science (2017)',
      },
      {
        classification: 'industry',
        stat: '63 %',
        headline: 'Propreté des chambres : premier déclencheur d\'avis négatifs',
        context: 'Les principaux déclencheurs d\'avis négatifs incluent la propreté des chambres (63 %), les odeurs (58 %) et les nuisibles (57 %), suivis de la propreté générale (42 %) et du bruit (31 %).',
        source: 'Ecolab / AHLA, résultats consommateurs hôtellerie (2025)',
      },
      {
        classification: 'consumer',
        stat: '93 %',
        headline: 'Voyageurs influencés par les avis en ligne',
        context: '93 % des voyageurs ont déclaré que les avis en ligne affectent leurs décisions de réservation.',
        source: 'TripAdvisor / recherche industrielle',
      },
      {
        classification: 'model',
        stat: '44 550 $–63 900 $',
        headline: 'Hausse illustrative annuelle des revenus de chambre',
        context: 'Pour un hôtel illustratif de 100 chambres de catégorie intermédiaire générant 4,5 M$ en revenus annuels de chambre, une amélioration de 1 % du score de réputation en ligne pourrait se traduire par cette hausse annuelle. Il s\'agit d\'un modèle illustratif YUL ON TAP basé sur les élasticités RevPAR publiées de 0,99 %–1,42 % et les hypothèses énoncées — et non d\'un résultat directement mesuré.',
        source: 'Modèle illustratif YUL ON TAP basé sur les relations RevPAR de Cornell et études examinées par des pairs',
      },
    ],
  },

  restaurants: {
    en: [
      {
        classification: 'academic',
        stat: '5–9%',
        headline: 'Revenue increase per one-star improvement',
        context: 'Independent restaurants experienced a 5–9% revenue increase associated with a one-star rating improvement on Yelp.',
        source: 'Harvard Business School — Michael Luca, "Reviews, Reputation, and Revenue: The Case of Yelp.com"',
      },
      {
        classification: 'consumer',
        stat: '82%',
        headline: 'Read reviews before dining',
        context: '82% of customers read online reviews for local establishments before deciding where to eat, drink, and shop.',
        source: 'BrightLocal Consumer Review Survey',
      },
      {
        classification: 'consumer',
        stat: '72%',
        headline: 'Trust increased by positive reviews',
        context: '72% of customers report that positive reviews increase their trust in a local business.',
        source: 'BrightLocal Consumer Review Survey',
      },
      {
        classification: 'consumer',
        stat: '94%',
        headline: 'Avoidance rate from negative reviews',
        context: '94% of consumers report being convinced to avoid a business based on negative reviews.',
        source: 'ReviewTrackers Customer Reviews Report',
      },
      {
        classification: 'consumer',
        stat: '31%',
        headline: 'Increased spending at top-rated businesses',
        context: 'Consumers spend 31% more at businesses with excellent reviews compared to average-rated competitors.',
        source: 'Invespcro Online Customer Reviews Research',
      },
      {
        classification: 'industry',
        stat: '30 lost customers',
        headline: 'Estimated customer loss per negative review',
        context: 'Industry research estimates that a single negative review costs a business approximately 30 customers, translating to $1,200–$1,500 in lost revenue at a $40–$50 average ticket. This is a modeled estimate, not a directly measured outcome.',
        source: 'WowApps Restaurant Reviews Impact Study',
      },
      {
        classification: 'model',
        stat: '$4–$7.50',
        headline: 'Immediate return-visit value per positive review',
        context: 'Using conservative-to-aggressive assumptions for review conversion rates (10%–50%) and redemption rates (10%–15%), each positive review generates an estimated $4–$7.50 in immediate return-visit revenue.',
        source: 'YUL ON TAP return-visit value model',
      },
      {
        classification: 'model',
        stat: '$50–$200+',
        headline: 'Long-term customer lifetime value per positive review',
        context: 'If a positive review converts a new regular customer visiting 2x/month over 12 months, the lifetime value compounds to $960–$1,200 annually, or $2,000–$3,600+ over 2–3 years.',
        source: 'YUL ON TAP model based on industry CLV ranges',
      },
    ],
    fr: [
      {
        classification: 'academic',
        stat: '5–9 %',
        headline: 'Augmentation des revenus par amélioration d\'une étoile',
        context: 'Les restaurants indépendants ont connu une augmentation de revenus de 5 à 9 % associée à une amélioration d\'une étoile de leur note sur Yelp.',
        source: 'Harvard Business School — Michael Luca, "Reviews, Reputation, and Revenue: The Case of Yelp.com"',
      },
      {
        classification: 'consumer',
        stat: '82 %',
        headline: 'Lisent les avis avant de choisir un restaurant',
        context: '82 % des consommateurs lisent les avis en ligne pour les établissements locaux avant de décider où manger, boire et faire leurs achats.',
        source: 'BrightLocal Consumer Review Survey',
      },
      {
        classification: 'consumer',
        stat: '72 %',
        headline: 'Confiance accrue par les avis positifs',
        context: '72 % des consommateurs déclarent que les avis positifs augmentent leur confiance envers une entreprise locale.',
        source: 'BrightLocal Consumer Review Survey',
      },
      {
        classification: 'consumer',
        stat: '94 %',
        headline: 'Taux d\'évitement suite aux avis négatifs',
        context: '94 % des consommateurs déclarent avoir été convaincus d\'éviter une entreprise en raison d\'avis négatifs.',
        source: 'ReviewTrackers Customer Reviews Report',
      },
      {
        classification: 'consumer',
        stat: '31 %',
        headline: 'Dépenses accrues dans les établissements les mieux notés',
        context: 'Les consommateurs dépensent 31 % de plus dans les entreprises ayant d\'excellents avis par rapport aux concurrents à notation moyenne.',
        source: 'Invespcro Online Customer Reviews Research',
      },
      {
        classification: 'industry',
        stat: '30 clients perdus',
        headline: 'Perte estimée de clients par avis négatif',
        context: 'La recherche industrielle estime qu\'un seul avis négatif coûte à une entreprise environ 30 clients, soit 1 200 $–1 500 $ de revenus perdus avec un ticket moyen de 40 $–50 $. Il s\'agit d\'une estimation modélisée, et non d\'un résultat directement mesuré.',
        source: 'WowApps Restaurant Reviews Impact Study',
      },
      {
        classification: 'model',
        stat: '4 $–7,50 $',
        headline: 'Valeur immédiate de visite retour par avis positif',
        context: 'En utilisant des hypothèses conservatrices à agressives pour les taux de conversion d\'avis (10 %–50 %) et les taux de rachat (10 %–15 %), chaque avis positif génère un estimé de 4 $–7,50 $ en revenus immédiats de visite retour.',
        source: 'Modèle de valeur de visite retour YUL ON TAP',
      },
      {
        classification: 'model',
        stat: '50 $–200 $+',
        headline: 'Valeur à vie du client par avis positif',
        context: 'Si un avis positif convertit un nouveau client régulier visitant 2x/mois sur 12 mois, la valeur à vie se compose à 960 $–1 200 $ annuellement, ou 2 000 $–3 600 $+ sur 2 à 3 ans.',
        source: 'Modèle YUL ON TAP basé sur les fourchettes de VVC de l\'industrie',
      },
    ],
  },

  medical: {
    en: [
      {
        classification: 'academic',
        stat: '32%',
        headline: 'Patients who posted a negative review',
        context: 'In a global survey of 1,612 cosmetic-neurotoxin patients, 32% said they posted a negative review online after their least-positive experience.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1,612',
      },
      {
        classification: 'academic',
        stat: '30%',
        headline: 'Patients who planned to switch providers',
        context: '30% of surveyed patients said they planned to switch healthcare providers after their least-positive cosmetic-neurotoxin experience.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1,612',
      },
      {
        classification: 'academic',
        stat: '52%',
        headline: 'Patients who discouraged others',
        context: 'Among patients who shared their least-positive experience, 52% said they discouraged others from using the same provider.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1,612',
      },
      {
        classification: 'academic',
        stat: '13%',
        headline: 'Patients who stopped treatment entirely',
        context: '13% of patients said they stopped receiving cosmetic-neurotoxin treatments altogether after a negative experience.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1,612',
      },
      {
        classification: 'industry',
        stat: '$527',
        headline: 'Average medical-spa patient spend per visit',
        context: 'The American Med Spa Association reported an average patient spend of $527 per visit. This figure is used as an input assumption ($525 rounded) in the YUL ON TAP revenue-at-risk model below.',
        source: 'American Med Spa Association, State of the Industry Report (2024)',
      },
      {
        classification: 'model',
        stat: '$15,750',
        headline: 'Modeled revenue at risk per negative review',
        context: 'Modeled as 30 potential lost customers multiplied by $525 average visit value. The 30-customer-loss assumption is adapted from restaurant industry research and is a model input — not an empirically measured medical-aesthetics finding. The $525 figure is a rounded benchmark from the American Med Spa Association report.',
        source: 'YUL ON TAP model using American Med Spa Association benchmark and restaurant-derived customer-loss assumption',
      },
      {
        classification: 'model',
        stat: '$5,250–$26,250',
        headline: 'Sensitivity analysis — revenue at risk',
        context: 'What if the 30-customer assumption is too high or too low? At 10 lost customers: $5,250. At 20: $10,500. At 30 (base model): $15,750. At 40: $21,000. At 50: $26,250. All at $525 per visit.',
        source: 'YUL ON TAP sensitivity model',
      },
    ],
    fr: [
      {
        classification: 'academic',
        stat: '32 %',
        headline: 'Patients ayant publié un avis négatif',
        context: 'Dans un sondage mondial auprès de 1 612 patients en neurotoxine cosmétique, 32 % ont déclaré avoir publié un avis négatif en ligne après leur expérience la moins positive.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1 612',
      },
      {
        classification: 'academic',
        stat: '30 %',
        headline: 'Patients planifiant de changer de prestataire',
        context: '30 % des patients sondés ont déclaré planifier de changer de prestataire de soins après leur expérience la moins positive en neurotoxine cosmétique.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1 612',
      },
      {
        classification: 'academic',
        stat: '52 %',
        headline: 'Patients ayant découragé d\'autres personnes',
        context: 'Parmi les patients ayant partagé leur expérience la moins positive, 52 % ont déclaré avoir découragé d\'autres personnes d\'utiliser le même prestataire.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1 612',
      },
      {
        classification: 'academic',
        stat: '13 %',
        headline: 'Patients ayant cessé tout traitement',
        context: '13 % des patients ont déclaré avoir complètement cessé les traitements de neurotoxine cosmétique après une expérience négative.',
        source: 'Aesthetic Surgery Journal Open Forum (2025), n=1 612',
      },
      {
        classification: 'industry',
        stat: '527 $',
        headline: 'Dépense moyenne par visite en médispa',
        context: 'L\'American Med Spa Association a rapporté une dépense moyenne par patient de 527 $ par visite. Ce chiffre est utilisé comme hypothèse d\'entrée (525 $ arrondi) dans le modèle de revenus à risque YUL ON TAP ci-dessous.',
        source: 'American Med Spa Association, State of the Industry Report (2024)',
      },
      {
        classification: 'model',
        stat: '15 750 $',
        headline: 'Revenus à risque modélisés par avis négatif',
        context: 'Modélisé comme 30 clients potentiels perdus multipliés par 525 $ de valeur moyenne par visite. L\'hypothèse de perte de 30 clients est adaptée de la recherche sur l\'industrie de la restauration et constitue un paramètre du modèle — et non un résultat empiriquement mesuré pour l\'esthétique médicale. Le chiffre de 525 $ est un référentiel arrondi du rapport de l\'American Med Spa Association.',
        source: 'Modèle YUL ON TAP utilisant le référentiel de l\'American Med Spa Association et l\'hypothèse de perte de clients dérivée de la restauration',
      },
      {
        classification: 'model',
        stat: '5 250 $–26 250 $',
        headline: 'Analyse de sensibilité — revenus à risque',
        context: 'Et si l\'hypothèse de 30 clients était trop élevée ou trop basse? À 10 clients perdus : 5 250 $. À 20 : 10 500 $. À 30 (modèle de base) : 15 750 $. À 40 : 21 000 $. À 50 : 26 250 $. Le tout à 525 $ par visite.',
        source: 'Modèle de sensibilité YUL ON TAP',
      },
    ],
  },
};

/* ════════════════════════════════════════════════════════════════
   CLASSIFICATION CONFIG
   ════════════════════════════════════════════════════════════════ */

const classificationStyles = {
  academic: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-300',
    border: 'border-blue-500/25',
    dot: 'bg-blue-400',
  },
  industry: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/25',
    dot: 'bg-amber-400',
  },
  consumer: {
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-300',
    border: 'border-emerald-500/25',
    dot: 'bg-emerald-400',
  },
  model: {
    bg: 'bg-rose-500/15',
    text: 'text-rose-300',
    border: 'border-rose-500/25',
    dot: 'bg-rose-400',
  },
};

/* ════════════════════════════════════════════════════════════════
   EVIDENCE CARD COMPONENT
   ════════════════════════════════════════════════════════════════ */

const EvidenceCard = ({ card, classLabel, sourceLabel, sourcesLabel }) => {
  const style = classificationStyles[card.classification];
  const isMultipleSources = card.source.includes(';');
  const label = isMultipleSources ? sourcesLabel : sourceLabel;
  return (
    <div
      data-testid={`evidence-card-${card.classification}`}
      className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 lg:p-8 hover:border-white/20 transition-all duration-300 flex flex-col"
    >
      {/* Classification badge */}
      <span className={`inline-flex items-center gap-1.5 self-start px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${style.bg} ${style.text} ${style.border} mb-5`}>
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
        {classLabel}
      </span>

      {/* Stat */}
      <p className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight">
        {card.stat}
      </p>

      {/* Headline */}
      <h3 className="text-base font-medium text-white/80 mb-4">
        {card.headline}
      </h3>

      {/* Context */}
      <p className="text-sm text-white/50 leading-relaxed mb-5 flex-1">
        {card.context}
      </p>

      {/* Source */}
      <p className="text-xs text-white/30 leading-relaxed border-t border-white/5 pt-4">
        <span className="font-semibold text-white/80">{label}:</span>{' '}
        {card.source}
      </p>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ════════════════════════════════════════════════════════════════ */

const SupportingDataPage = () => {
  const { language, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('hotels');

  const tabs = [
    { key: 'hotels', icon: Building2, label: t('supportingData.tabHotels') },
    { key: 'restaurants', icon: UtensilsCrossed, label: t('supportingData.tabRestaurants') },
    { key: 'medical', icon: Stethoscope, label: t('supportingData.tabMedical') },
  ];

  const classLabels = {
    academic: t('supportingData.classAcademic'),
    industry: t('supportingData.classIndustry'),
    consumer: t('supportingData.classConsumer'),
    model: t('supportingData.classModel'),
  };

  const sourceLabel = t('supportingData.sourceLabel');
  const sourcesLabel = t('supportingData.sourcesLabel');
  const cards = evidenceData[activeTab]?.[language] || evidenceData[activeTab]?.en || [];

  return (
    <div data-testid="supporting-data-page" className="bg-[#0D0D0D] min-h-screen">

      {/* ─── Hero Section ─── */}
      <section className="relative pt-16 pb-12 lg:pt-24 lg:pb-16 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-wine/6 rounded-full blur-[120px]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 text-xs font-bold uppercase tracking-[0.2em] text-white/50">
              <BookOpen className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t('supportingData.title')}
            </span>
          </div>
          <h1
            data-testid="supporting-data-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.1] mb-6"
          >
            {t('supportingData.subtitle')}
          </h1>
          <p className="text-base sm:text-lg text-white/50 leading-relaxed max-w-3xl mx-auto">
            {t('supportingData.intro')}
          </p>
        </div>
      </section>

      {/* ─── Classification Legend ─── */}
      <section className="pb-8 lg:pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            {Object.entries(classLabels).map(([key, label]) => {
              const style = classificationStyles[key];
              return (
                <div key={key} className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
                  <span className="text-xs text-white/40 font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Industry Tabs ─── */}
      <section className="pb-12 lg:pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid="industry-tabs"
            className="flex justify-center"
          >
            <div className="inline-flex bg-white/5 border border-white/10 rounded-xl p-1.5 gap-1">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    data-testid={`tab-${tab.key}`}
                    className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-wine text-white shadow-lg shadow-wine/20'
                        : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" strokeWidth={1.5} />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Evidence Cards Grid ─── */}
      <section className="pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <CardGrid layout="1-md2" testId="supporting-data-cards-grid">
            {cards.map((card, idx) => (
              <EvidenceCard
                key={`${activeTab}-${idx}`}
                card={card}
                classLabel={classLabels[card.classification]}
                sourceLabel={sourceLabel}
                sourcesLabel={sourcesLabel}
              />
            ))}
          </CardGrid>
        </div>
      </section>

      {/* ─── Methodology Note ─── */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            data-testid="methodology-note"
            className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 lg:p-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <FlaskConical className="w-5 h-5 text-white/40" strokeWidth={1.5} />
              <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">
                {t('supportingData.methodologyTitle')}
              </h3>
            </div>
            <p className="text-sm text-white/40 leading-relaxed">
              {t('supportingData.methodologyText')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportingDataPage;
